/**
 * ChunkErrorBoundary Component
 *
 * React Error Boundary that catches webpack chunk loading errors and gracefully
 * handles them by reloading the page. This is particularly important after
 * deployments when old chunk files are no longer available on the server.
 *
 * Features:
 * - Detects ChunkLoadError specifically
 * - Automatic reload on chunk errors (with safety limits)
 * - Prevents infinite reload loops
 * - Shows fallback UI if auto-reload fails
 * - Resets error state on successful mount
 *
 * Usage:
 *   <ChunkErrorBoundary>
 *     <App />
 *   </ChunkErrorBoundary>
 */

import React from 'react';
import PropTypes from 'prop-types';
import './ChunkErrorBoundary.css';

const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_HISTORY_KEY = 'chunk-error-reload-history';
const RELOAD_WINDOW = 60000; // 1 minute window

export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reloadAttempts: 0,
      maxAttemptsReached: false,
    };
  }

  /**
   * Update state when an error is caught
   * Checks if error is a chunk loading error
   */
  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('ChunkLoadError') ||
      error?.message?.toLowerCase().includes('failed to fetch');

    if (isChunkError) {
      console.error('[ChunkErrorBoundary] Chunk loading error detected:', error);
      return {
        hasError: true,
        error,
      };
    }

    // Not a chunk error - let it bubble up to other error boundaries
    return null;
  }

  /**
   * Handle the error after component tree update
   * Attempts automatic reload with safety limits using timestamp-based tracking
   */
  componentDidCatch(error, errorInfo) {
    if (!this.state.hasError) {
      // Not a chunk error, should not be caught here
      // getDerivedStateFromError returned null, so this shouldn't happen
      console.error('[ChunkErrorBoundary] Non-chunk error in componentDidCatch:', error);
      return;
    }

    console.error('[ChunkErrorBoundary] Error details:', error, errorInfo);

    const now = Date.now();

    // Get reload history from session storage
    let reloadHistory = [];
    try {
      const historyStr = sessionStorage.getItem(RELOAD_HISTORY_KEY);
      if (historyStr) {
        reloadHistory = JSON.parse(historyStr);
        // Filter out old reloads outside the time window
        reloadHistory = reloadHistory.filter(timestamp => now - timestamp < RELOAD_WINDOW);
      }
    } catch (error) {
      console.error('[ChunkErrorBoundary] Error parsing reload history:', error);
      reloadHistory = [];
    }

    console.log('[ChunkErrorBoundary] Recent reload attempts in last minute:', reloadHistory.length);

    // Check if we've exceeded max attempts within the time window
    if (reloadHistory.length >= MAX_RELOAD_ATTEMPTS) {
      console.error('[ChunkErrorBoundary] Max reload attempts reached within time window');
      this.setState({
        reloadAttempts: reloadHistory.length,
        maxAttemptsReached: true,
        errorInfo,
      });
      // Clear the history so user can try again after the time window
      sessionStorage.removeItem(RELOAD_HISTORY_KEY);
      return;
    }

    // Add current timestamp to history
    reloadHistory.push(now);

    try {
      sessionStorage.setItem(RELOAD_HISTORY_KEY, JSON.stringify(reloadHistory));
    } catch (error) {
      console.error('[ChunkErrorBoundary] Error saving reload history:', error);
    }

    console.log('[ChunkErrorBoundary] Attempting reload', reloadHistory.length, 'of', MAX_RELOAD_ATTEMPTS);

    // Perform reload after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  /**
   * Clean up old reload history on successful mount
   * This ensures expired timestamps don't accumulate
   */
  componentDidMount() {
    try {
      const historyStr = sessionStorage.getItem(RELOAD_HISTORY_KEY);
      if (historyStr) {
        const now = Date.now();
        let reloadHistory = JSON.parse(historyStr);
        // Filter out old entries
        reloadHistory = reloadHistory.filter(timestamp => now - timestamp < RELOAD_WINDOW);

        if (reloadHistory.length === 0) {
          // All entries expired, remove the key
          sessionStorage.removeItem(RELOAD_HISTORY_KEY);
        } else {
          // Update with filtered history
          sessionStorage.setItem(RELOAD_HISTORY_KEY, JSON.stringify(reloadHistory));
        }
      }
    } catch (error) {
      console.error('[ChunkErrorBoundary] Error cleaning reload history:', error);
    }
  }

  /**
   * Manual reload handler for fallback UI
   */
  handleManualReload = () => {
    console.log('[ChunkErrorBoundary] Manual reload triggered');
    sessionStorage.removeItem(RELOAD_HISTORY_KEY);
    window.location.reload();
  };

  render() {
    // If no chunk error, render children normally
    if (!this.state.hasError) {
      return this.props.children;
    }

    // If max attempts not reached, show loading state while reloading
    if (!this.state.maxAttemptsReached) {
      return (
        <div className="chunk-error-boundary">
          <div className="chunk-error-boundary__content">
            <div className="chunk-error-boundary__spinner" role="status" aria-live="polite">
              <svg
                className="chunk-error-boundary__spinner-svg"
                viewBox="0 0 50 50"
              >
                <circle
                  className="chunk-error-boundary__spinner-circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
            </div>
            <h2 className="chunk-error-boundary__title">Loading Update...</h2>
            <p className="chunk-error-boundary__message">
              The application is updating to the latest version.
              This will only take a moment.
            </p>
          </div>
        </div>
      );
    }

    // Max attempts reached - show error UI with manual reload option
    return (
      <div className="chunk-error-boundary chunk-error-boundary--error">
        <div className="chunk-error-boundary__content">
          <div className="chunk-error-boundary__icon" role="img" aria-label="Error">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>

          <h2 className="chunk-error-boundary__title">
            Unable to Load Application
          </h2>

          <p className="chunk-error-boundary__message">
            We encountered an issue loading the latest version of the application.
            This usually happens after an update. Please try reloading the page.
          </p>

          <div className="chunk-error-boundary__actions">
            <button
              className="chunk-error-boundary__button chunk-error-boundary__button--primary"
              onClick={this.handleManualReload}
            >
              Reload Page
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="chunk-error-boundary__details">
              <summary>Error Details (Development Only)</summary>
              <pre className="chunk-error-boundary__error-stack">
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

ChunkErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};
